/* eslint-disable @next/next/no-img-element */
"use client";
import OfficeLocation from "@/common/OfficeLocation";
import AddDelivery from "@/components/Delivery/AddDelivery";
import ViewDelivery from "@/components/Delivery/ViewDelivery";
import Layout from "@/components/Layout/Layout";
import { CMSModal } from "@/context";
import Button from "@/designs/Button";
import DateRangePicker from "@/designs/DateRangePicker";
import Input from "@/designs/Input";
import Pagination from "@/designs/Pagination";
import Select from "@/designs/Select";
import Table from "@/designs/Table";
import {
  GetDeliveriesDocument,
  useGetDeliveriesQuery,
  useUpdateDeliveryMutation,
} from "@/graphql/generated/schema";
import { AnimatePresence, motion } from "framer-motion";
import { CircleCheck, Package, Search, SlidersHorizontal } from "lucide-react";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
const DeliveryPage = () => {
  const { t } = useTranslation();
  const deliveryFilter = [
    { label: t("deliveries.filters.all"), value: "" },
    {
      label: t("deliveries.filters.delivered"),
      value: "delivered",
    },
    { label: t("deliveries.filters.collected"), value: "collected" },
  ];
  const { theme, selectedLocation } = useContext(CMSModal);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [addDelivery, setAddDelivery] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [edit, setEdit] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<any>(null);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 350);

  const [filters, setFilters] = useState({
    search: "",
    status: "All",
    category: "",
    startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    pageSize: 50,
    page: 1,
    sorted: {
      direction: "",
      columnId: "",
    },
    location: selectedLocation,
  });

  useEffect(() => {
    setFilters((prev) => ({ ...prev, search: debouncedSearch }));
  }, [debouncedSearch]);

  React.useEffect(() => {
    setFilters((prev) => ({ ...prev, location: selectedLocation }));
  }, [selectedLocation]);

  const [updateDelivery] = useUpdateDeliveryMutation({
    refetchQueries: [GetDeliveriesDocument],
  });

  const handleMarkAsCollected = async () => {
    const uncollected = selectedRows.filter((r: any) => !r.collected);
    await Promise.all(
      uncollected.map((r: any) =>
        updateDelivery({
          variables: {
            input: { _id: r.id, collected: true, location: r.location?.id },
          },
        }),
      ),
    );
    setSelectedRows([]);
  };

  const { data, loading } = useGetDeliveriesQuery({
    variables: {
      search: filters.search,
      limit: filters.pageSize,
      offset: (filters.page - 1) * filters.pageSize,
      startDate: filters.startDate,
      endDate: filters.endDate,
      collected:
        filters.status === "All"
          ? undefined
          : filters.status === "delivered"
            ? false
            : true,
      sorted: filters.sorted,
      location: filters.location,
    },
    pollInterval: 5000,
  });

  const deliveries = data?.getDeliveries?.delivery ?? [];
  const deliveryCount = data?.getDeliveries?.count || 0;
  const totalPages = Math.ceil(deliveryCount / filters.pageSize);
  const columns = [
    {
      id: "reciepient",
      header: t("deliveries.columns.recipient"),
      accessor: "reciepient",
      render: (_row: any, value: any) => (
        <div className="flex items-center gap-4 py-3">
          <div
            className={twMerge(
              "flex gap-1 items-center justify-center w-14 h-14 rounded-full overflow-hidden border shadow-sm transition-colors",
              theme === "dark" ? "border-gray-400" : "border-gray-300",
            )}
          >
            {value?.deliveryType === "recipient" ? (
              <img
                src={value?.reciepient?.img || "/Avatar.svg"}
                alt="UserImg"
                className="w-full h-full object-cover"
              />
            ) : (
              <Package />
            )}
          </div>
          {value?.deliveryType === "recipient" ? (
            <h4 className="text-xs truncate">
              {value.reciepient.firstName} {value.reciepient.lastName}
            </h4>
          ) : (
            <h4 className="text-xs truncate">
              {t("deliveries.viewDelivery.generalDelivery")}
            </h4>
          )}
        </div>
      ),
      sortValue: (row: any) => row.clientCompanyInfo?.clientName || "",
    },
    {
      id: "delivered",
      header: t("deliveries.columns.delivered"),
      accessor: "delivered",
      render: (value: any) => moment(value).format("h:mma, MMM Do") || "-",
      sortable: true,
    },
    {
      id: "signature",
      header: t("deliveries.columns.signature"),
      accessor: "signature",
      render: (value: any) => (value ? "Required" : "-"),
    },
    {
      id: "collected",
      header: t("deliveries.columns.collected"),
      accessor: "collected",
      render: (value: any) => (value ? <CircleCheck color="green" /> : "-"),
      sortable: true,
    },
  ];
  return (
    <Layout>
      <div
        className={twMerge(
          "m-4 lg:m-10 p-4 lg:p-10 space-y-4 transition-colors shadow rounded",
          theme === "dark" ? "bg-[#191919]" : "bg-white",
        )}
      >
        <div className="flex flex-wrap items-center justify-between">
          <h2
            className={twMerge(
              "text-lg md:text-3xl font-semibold py-4",
              theme === "dark" ? "text-gray-100" : "text-[#3b3b3b]",
            )}
          >
            {t("deliveries.title")} <OfficeLocation />
          </h2>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <Input
              placeholder={t("deliveries.search")}
              icon={<Search size={18} />}
              value={searchInput}
              onChange={setSearchInput}
            />
            <DateRangePicker
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChange={(start, end) =>
                setFilters({ ...filters, startDate: start, endDate: end })
              }
            />
            <button
              className="text-[#099ed1] flex items-center gap-1 font-bold"
              onClick={() => setIsFilterOpen((p) => !p)}
            >
              <SlidersHorizontal size={14} />
              {t("deliveries.filters.button")}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Button
              className="w-auto px-3 lg:px-4 whitespace-nowrap text-xs font-medium"
              variant="outline"
              disabled={selectedRows.length === 0 || selectedRows.every((r: any) => r.collected)}
              onClick={handleMarkAsCollected}
            >
              <CircleCheck size={14} />
              {t("deliveries.markAsCollected")}
            </Button>
            <Button className="w-32 lg:w-48" onClick={() => setAddDelivery(true)}>
              {t("deliveries.filters.delivered") === "Delivered"
                ? "Add Delivery"
                : t("deliveries.addDelivery.title")}
            </Button>
          </div>
        </div>
        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                height: { duration: 0.25, ease: "easeInOut" },
                opacity: { duration: 0.15, ease: "easeInOut" },
              }}
              className="overflow-hidden mb-4"
            >
              <div
                className={twMerge(
                  "border p-4 flex gap-4 items-center",
                  theme === "dark"
                    ? "bg-[#262727] border-gray-700"
                    : "bg-[#f5fbfd] border-[#c4e7f5]",
                )}
              >
                <Select
                  label={t("deliveries.filters.status")}
                  value={filters.status}
                  options={deliveryFilter}
                  onChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                  className="md:w-50"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <Table
          data={deliveries}
          columns={columns}
          loading={loading}
          emptyObject={{
            icon: <Package size={32} />,
            title: t("deliveries.emptyState.title"),
          }}
          setSelectedRow={(row: any) => {
            setSelectedDelivery(row);
            setEdit(true);
          }}
          selectable={{
            active: true,
            selectedRows,
            setSelectedRows,
            rowIdKey: "id", // ✅ Type-safe: "_id" exists in InvoiceRow
          }}
          setSorted={(value) =>
            setFilters((prev) => ({
              ...prev,
              sorted: value,
            }))
          }
        />
        {deliveries && deliveries.length && (
          <Pagination
            page={filters.page}
            totalPages={totalPages}
            pageSize={filters.pageSize}
            onPageChange={(page) => setFilters({ ...filters, page })}
            onPageSizeChange={(pageSize) =>
              setFilters({ ...filters, pageSize })
            }
          />
        )}
        <AddDelivery
          isOpen={addDelivery}
          onClose={() => setAddDelivery(false)}
        />

        <ViewDelivery
          isOpen={edit}
          onClose={() => setEdit(false)}
          delivery={selectedDelivery}
          allDeliveries={deliveries}
          setDelivery={setSelectedDelivery}
        />
      </div>
    </Layout>
  );
};

export default DeliveryPage;
